import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Heading } from '../../components/ui'
import GameJogarTab, { type GameHudStats } from './GameJogarTab'
import { formatGameClock } from './gamePuzzle'

interface GameJogarModalProps {
  open: boolean
  onClose: () => void
  onShareResult?: (text: string) => void
}

const emptyHud: GameHudStats = {
  visible: false,
  elapsedMs: 0,
  found: 0,
  total: 0,
  compact: true,
}

const GameJogarModal: React.FC<GameJogarModalProps> = ({ open, onClose, onShareResult }) => {
  const [hud, setHud] = useState<GameHudStats>(emptyHud)

  const handleClose = () => {
    setHud(emptyHud)
    onClose()
  }

  const handleHudChange = useCallback((stats: GameHudStats) => {
    setHud(stats)
  }, [])

  const handleShareResult = (text: string) => {
    setHud(emptyHud)
    onClose()
    onShareResult?.(text)
  }

  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  if (!open) return null

  const compact = hud.compact !== false

  return createPortal(
    <div
      className={
        compact
          ? 'fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-black/50 p-4 sm:p-6'
          : 'fixed inset-0 z-[100] flex justify-center bg-black/50'
      }
      onClick={handleClose}
    >
      <div
        className={
          compact
            ? 'my-auto flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800'
            : 'flex h-full min-h-0 w-full max-w-7xl flex-col overflow-hidden bg-white shadow-xl dark:bg-gray-800'
        }
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800 sm:px-5">
          <Heading as="h2" size="xl" className="shrink-0 text-gray-900 dark:text-white">
            Jogar caça-palavras
          </Heading>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-2 sm:gap-3">
            {hud.visible && hud.total > 0 && hud.found >= hud.total && (
              <div className="hidden rounded-lg border border-teal-300 bg-teal-50 px-3 py-1.5 text-center dark:border-teal-700 dark:bg-teal-900/40 sm:block">
                <div className="text-sm font-extrabold text-[#016B76] dark:text-teal-200">
                  Jogo concluído!
                </div>
              </div>
            )}
            {hud.visible && (
              <>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-center dark:border-gray-600 dark:bg-gray-900/40">
                  <div className="text-[9px] font-bold uppercase tracking-wide text-gray-500">
                    Tempo
                  </div>
                  <div className="text-base font-extrabold tabular-nums text-[#016B76] dark:text-teal-300 sm:text-lg">
                    {formatGameClock(hud.elapsedMs)}
                  </div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-center dark:border-gray-600 dark:bg-gray-900/40">
                  <div className="text-[9px] font-bold uppercase tracking-wide text-gray-500">
                    Encontradas
                  </div>
                  <div className="text-base font-extrabold tabular-nums text-[#016B76] dark:text-teal-300 sm:text-lg">
                    {hud.found}/{hud.total}
                  </div>
                </div>
              </>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
              aria-label="Fechar"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div
          className={
            compact
              ? 'overflow-y-auto px-3 py-4 sm:px-5'
              : 'min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-5'
          }
        >
          <GameJogarTab onHudChange={handleHudChange} onShareResult={handleShareResult} />
        </div>
      </div>
    </div>,
    document.body
  )
}

export default GameJogarModal
